import { useEffect, useMemo, useState } from 'react';

/**
 * Linha do tempo dos workers.
 *
 * A tabela responde "qual o estado de cada worker". Esta visão responde outra
 * pergunta, que a tabela não responde bem: "o que acabou de rodar, o que está
 * rodando agora, e o que vem a seguir" — tudo na mesma régua de tempo.
 *
 * ── A regra que decide o desenho ────────────────────────────────────────────
 * O passado é FATO e o futuro é PROMESSA, então os dois não podem ser desenhados
 * do mesmo jeito:
 *
 *   · à ESQUERDA do ponteiro    → barra com a duração REAL da execução
 *   · sobre o ponteiro (rodando) → barra que começou no last_run e cresce até agora
 *   · à DIREITA do ponteiro      → só um MARCADOR de quando começa, sem barra
 *
 * Desenhar uma barra estimada no futuro seria dizer "vai demorar tanto" com uma
 * confiança que não temos: a duração varia com o tamanho da base e com a API do
 * ML respondendo mais devagar. O marcador promete só o que dá para prometer —
 * a hora de início.
 */

const JANELA_MS = 60 * 60 * 1000; // 1h para cada lado do agora
const ALTURA_LINHA = 26;
const LARGURA_ROTULO = 200;

const fmtHora = (ms) =>
    new Date(ms).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', hour12: false });

const fmtDuracao = (ms) => {
    if (!Number.isFinite(ms) || ms <= 0) return null;
    if (ms < 1000) return `${Math.round(ms)}ms`;
    if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
};

/** Rótulo curto: `sync_campaigns_worker` → `campaigns`. */
const rotuloCurto = (nome) =>
    String(nome || '')
        .replace(/^sync_/, '')
        .replace(/_worker$/, '')
        .replace(/_/g, ' ');

const TONS = {
    RUNNING: 'bg-info-500',
    IDLE: 'bg-success-500',
    FAILED: 'bg-danger-500',
};

const WorkersTimeline = ({ jobs = [], onRun, running = {} }) => {
    // O ponteiro precisa andar sozinho, senão a régua congela entre os refreshes
    // do /health (30s) e o "agora" mente por até meio minuto.
    const [agora, setAgora] = useState(() => Date.now());
    useEffect(() => {
        const t = setInterval(() => setAgora(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);

    const inicio = agora - JANELA_MS;
    const fim = agora + JANELA_MS;
    const posicao = (ms) => ((ms - inicio) / (fim - inicio)) * 100;

    const linhas = useMemo(() => {
        return jobs.map((job) => {
            const lastRun = job.last_run ? new Date(job.last_run).getTime() : null;
            const nextRun = job.next_run_at ? new Date(job.next_run_at).getTime() : null;
            const rodando = job.status === 'RUNNING';
            // Duração para a barra: a da ÚLTIMA execução se houver; senão a média.
            // Nunca uma estimativa inventada — sem nenhuma das duas, a barra vira
            // um tracinho mínimo, que é honesto: "rodou, não sei por quanto tempo".
            const duracao = Number(job.last_duration_ms) || Number(job.avg_duration_ms) || null;

            return {
                ...job,
                lastRun,
                nextRun,
                rodando,
                duracao,
                // Enquanto roda, a barra vai do início até AGORA — é o que faz o
                // worker "mudar de posição" e mostrar que está em execução, em vez
                // de ficar parado onde terminou da última vez.
                barraFim: rodando ? agora : lastRun !== null && duracao ? lastRun + duracao : lastRun,
            };
        });
    }, [jobs, agora]);

    // Marcas de hora cheia e meia hora, para a régua ter referência.
    const marcas = useMemo(() => {
        const passo = 15 * 60 * 1000;
        const primeira = Math.ceil(inicio / passo) * passo;
        const out = [];
        for (let t = primeira; t <= fim; t += passo) out.push(t);
        return out;
    }, [inicio, fim]);

    if (jobs.length === 0) {
        return <p className="px-1 py-6 text-center text-xs text-mono-400">Nenhum worker registrado.</p>;
    }

    return (
        <div className="overflow-x-auto">
            <div className="min-w-[760px]">
                {/* régua */}
                <div className="relative mb-1 h-5" style={{ marginLeft: LARGURA_ROTULO }}>
                    {marcas.map((t) => (
                        <span
                            key={t}
                            className="absolute -translate-x-1/2 text-[9px] tabular-nums text-mono-400"
                            style={{ left: `${posicao(t)}%` }}
                        >
                            {fmtHora(t)}
                        </span>
                    ))}
                </div>

                <div className="relative">
                    {/* ponteiro do agora, atravessando todas as linhas */}
                    <div
                        className="pointer-events-none absolute top-0 z-20 w-px bg-danger-500"
                        style={{
                            left: `calc(${LARGURA_ROTULO}px + ${posicao(agora)}% * (100% - ${LARGURA_ROTULO}px) / 100%)`,
                            height: linhas.length * ALTURA_LINHA,
                        }}
                        aria-hidden="true"
                    >
                        <span className="absolute -top-4 -translate-x-1/2 whitespace-nowrap rounded bg-danger-500 px-1 text-[9px] font-bold text-white">
                            {fmtHora(agora)}
                        </span>
                    </div>

                    {linhas.map((linha) => {
                        const temBarra = linha.barraFim !== null && linha.lastRun !== null;
                        const esq = temBarra ? Math.max(0, posicao(linha.lastRun)) : 0;
                        const dir = temBarra ? Math.min(100, posicao(linha.barraFim)) : 0;
                        // Largura mínima para a barra não sumir: uma execução de 30s
                        // numa régua de 2h tem 0,4% de largura e ficaria invisível.
                        const largura = temBarra ? Math.max(dir - esq, 0.6) : 0;
                        const foraDaJanela = temBarra && posicao(linha.barraFim) < 0;
                        const proximoVisivel =
                            linha.nextRun !== null && posicao(linha.nextRun) >= 0 && posicao(linha.nextRun) <= 100;
                        const proximoAlemDaJanela = linha.nextRun !== null && posicao(linha.nextRun) > 100;

                        return (
                            <div key={linha.job_name} className="flex items-center" style={{ height: ALTURA_LINHA }}>
                                <div className="flex shrink-0 items-center gap-1.5" style={{ width: LARGURA_ROTULO }}>
                                    <button
                                        type="button"
                                        onClick={() => onRun?.(linha.job_name)}
                                        disabled={linha.rodando || running[linha.job_name]}
                                        title={
                                            linha.rodando
                                                ? 'Já está executando'
                                                : `Executar ${linha.job_name} agora`
                                        }
                                        className="rounded border border-mono-300 px-1 text-[10px] leading-4 text-mono-600 transition hover:border-info-500 hover:text-info-600 disabled:cursor-not-allowed disabled:opacity-40 dark:border-mono-700 dark:text-mono-300"
                                    >
                                        {running[linha.job_name] ? '…' : '▶'}
                                    </button>
                                    <span
                                        className="truncate text-[11px] text-mono-700 dark:text-mono-200"
                                        title={linha.job_name}
                                    >
                                        {rotuloCurto(linha.job_name)}
                                    </span>
                                </div>

                                <div className="relative h-full flex-1 border-b border-mono-100 dark:border-mono-900">
                                    {/* PASSADO: barra com a duração real */}
                                    {temBarra && !foraDaJanela ? (
                                        <div
                                            className={`absolute top-1/2 h-2 -translate-y-1/2 rounded-sm ${
                                                TONS[linha.status] || 'bg-mono-400'
                                            } ${linha.rodando ? 'animate-pulse' : ''}`}
                                            style={{ left: `${esq}%`, width: `${largura}%` }}
                                            title={`${linha.job_name}\ninício ${fmtHora(linha.lastRun)}${
                                                linha.rodando
                                                    ? ' · EXECUTANDO AGORA'
                                                    : linha.duracao
                                                      ? ` · durou ${fmtDuracao(linha.duracao)}`
                                                      : ' · duração não registrada'
                                            }`}
                                        />
                                    ) : null}

                                    {/* fora da janela à esquerda: só a seta indicando que rodou antes */}
                                    {foraDaJanela ? (
                                        <span
                                            className="absolute left-0 top-1/2 -translate-y-1/2 text-[9px] text-mono-400"
                                            title={`Última execução ${fmtHora(linha.lastRun)} (fora da janela)`}
                                        >
                                            «
                                        </span>
                                    ) : null}

                                    {/* FUTURO: marcador de início, sem barra */}
                                    {proximoVisivel && !linha.rodando ? (
                                        <div
                                            className="absolute top-1/2 -translate-y-1/2"
                                            style={{ left: `${posicao(linha.nextRun)}%` }}
                                            title={`Próxima execução às ${fmtHora(linha.nextRun)}`}
                                        >
                                            <div className="h-3 w-px bg-mono-400" />
                                            <span className="absolute left-1 top-1/2 -translate-y-1/2 whitespace-nowrap text-[9px] tabular-nums text-mono-400">
                                                {fmtHora(linha.nextRun)}
                                            </span>
                                        </div>
                                    ) : null}

                                    {proximoAlemDaJanela ? (
                                        <span
                                            className="absolute right-0 top-1/2 -translate-y-1/2 text-[9px] text-mono-400"
                                            title={`Próxima execução ${fmtHora(linha.nextRun)} (fora da janela)`}
                                        >
                                            »
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-3 text-[9px] text-mono-400">
                    <span className="flex items-center gap-1">
                        <span className="h-2 w-4 rounded-sm bg-success-500" /> execução concluída (largura = duração real)
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="h-2 w-4 animate-pulse rounded-sm bg-info-500" /> executando agora
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="h-2 w-4 rounded-sm bg-danger-500" /> falhou
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="h-3 w-px bg-mono-400" /> próxima execução (só o início — não estimamos duração)
                    </span>
                </div>
            </div>
        </div>
    );
};

export default WorkersTimeline;
